import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import {
  getCart,
  removeFromCart,
  updateCartItemQuantity,
  clearCart,
  getCartTotal,
  CartItem,
  subscribeToCartUpdates
} from '../../Services/cartService';
import {
  getCountries,
  getStatesByCountry,
  calculateShipping,
  qualifiesForFreeShipping,
  Country,
  StateProvince,
  saveShippingInfo,
  ShippingInfo
} from '../../Services/shippingService';
import { calculateDiscountedPrice, hasDiscount } from '../../Services/productService';
import { useCompany } from '../../Contexts/CompanyContext';
import HeaderSpacer from '../Header/HeaderSpacer';

const PLACEHOLDER = '/asset/images/product-placeholder.png';

/* ================= IMAGE WITH PLACEHOLDER ================= */
interface CartImageProps {
  src?: string;
  alt: string;
}

const CartImage: React.FC<CartImageProps> = ({ src, alt }) => {
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
  }, [src]);

  const resolvedSrc = !src || error ? PLACEHOLDER : src;

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      onError={() => setError(true)}
    />
  );
};

const Cart: React.FC = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>('');

  // shippingCost = raw cost from calculateShipping (before free-shipping check)
  const [shippingCost, setShippingCost] = useState<number>(0);
  // freeShippingLimit = applicable threshold (state override or country fallback)
  const [freeShippingLimit, setFreeShippingLimit] = useState<number>(0);

  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<StateProvince[]>([]);
  const [loadingCountries, setLoadingCountries] = useState<boolean>(true);
  // shippingCalculated: true only after the user clicks "Update Totals"
  const [shippingCalculated, setShippingCalculated] = useState<boolean>(false);

  // ── Validation error states ───────────────────────────────────────────────
  const [countryError, setCountryError] = useState<string>('');
  const [stateError, setStateError] = useState<string>('');

  const { formatPrice, calculateTax, taxPercentage } = useCompany();

  // ── Load countries from Firestore (cached) ────────────────────────────────
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoadingCountries(true);
        const countriesData = await getCountries();
        setCountries(countriesData);
      } catch (error) {
        console.error('Error loading countries:', error);
      } finally {
        setLoadingCountries(false);
      }
    };
    loadCountries();
  }, []);

  // ── Subscribe to cart updates ─────────────────────────────────────────────
  useEffect(() => {
    setCartItems(getCart());
    const unsubscribe = subscribeToCartUpdates((updatedCart) => {
      setCartItems(updatedCart);
    });
    return unsubscribe;
  }, []);

  // ── When country changes, reload states and reset shipping ────────────────
  useEffect(() => {
    if (selectedCountry) {
      const countryStates = getStatesByCountry(selectedCountry, countries);
      setStates(countryStates);
      setSelectedState('');
      setShippingCalculated(false);
      setShippingCost(0);
      setFreeShippingLimit(0);
      // Clear country error when a valid country is selected
      setCountryError('');
      // Reset state error since state selection resets
      setStateError('');
    } else {
      setStates([]);
      setSelectedState('');
      setShippingCalculated(false);
      setShippingCost(0);
      setFreeShippingLimit(0);
    }
  }, [selectedCountry, countries]);

  // ── When state changes, clear state error and require re-calculation ──────
  useEffect(() => {
    if (selectedState) {
      setStateError('');
    }
    setShippingCalculated(false);
  }, [selectedState]);

  // ── When postal code changes, require re-calculation ─────────────────────
  useEffect(() => {
    setShippingCalculated(false);
  }, [postalCode]);

  // ── Cart item handlers ────────────────────────────────────────────────────
  const handleRemoveItem = (cartItemId: string) => {
    if (window.confirm('Are you sure you want to remove this item from cart?')) {
      const result = removeFromCart(cartItemId);
      if (!result.success) alert(result.message);
    }
  };

  const handleUpdateQuantity = (cartItemId: string, newQuantity: number) => {
    const result = updateCartItemQuantity(cartItemId, newQuantity);
    if (!result.success) alert(result.message);
  };

  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to clear your entire cart?')) {
      clearCart();
      setShippingCost(0);
      setFreeShippingLimit(0);
      setSelectedCountry('');
      setSelectedState('');
      setPostalCode('');
      setShippingCalculated(false);
      setCountryError('');
      setStateError('');
    }
  };

  // ── "Update Totals" — validate then calculate shipping ───────────────────
  const handleUpdateShipping = () => {
    // Reset errors first
    let hasError = false;

    if (!selectedCountry) {
      setCountryError('Please select a country.');
      hasError = true;
    } else {
      setCountryError('');
    }

    // Only validate state if the selected country actually has states
    if (states.length > 0 && !selectedState) {
      setStateError('Please select a state / province.');
      hasError = true;
    } else {
      setStateError('');
    }

    if (hasError) return;

    const shipping = calculateShipping(selectedCountry, selectedState, postalCode, countries);

    // Update state — these drive isFreeShipping and finalShippingCost below
    setShippingCost(shipping.shippingCost);
    setFreeShippingLimit(shipping.freeShippingLimit);
    setShippingCalculated(true);

    // Determine whether this order qualifies for free shipping right now
    // (we can't use the state variables yet since setState is async)
    const currentSubtotal = getCartTotal();
    const qualifies =
      shipping.freeShippingLimit > 0 && currentSubtotal >= shipping.freeShippingLimit;

    if (qualifies) {
      alert(`🎉 Great news! Your order qualifies for FREE shipping!`);
    } else {
      alert(`Shipping cost updated: ${formatPrice(shipping.shippingCost)}`);
    }
  };

  // ── Navigate to checkout, saving full shipping info ───────────────────────
  const handleCheckout = () => {
    if (cartItems.length === 0) {
      alert('Your cart is empty!');
      return;
    }
    if (!shippingCalculated) {
      alert('Please calculate shipping before proceeding to checkout');
      return;
    }

    // Resolve human-readable names so Checkout can pre-fill selects and labels
    const countryObj = countries.find(c => c.id === selectedCountry);
    const stateObj = states.find(s => s.id === selectedState);

    const shippingInfo: ShippingInfo = {
      countryId: selectedCountry,
      countryName: countryObj?.name ?? '',
      stateId: selectedState,
      stateName: stateObj?.name ?? '',
      postalCode,
      shippingCost: finalShippingCost,   // save the actual charged amount
      freeShippingLimit,                 // save the threshold so Checkout can recalculate
    };
    saveShippingInfo(shippingInfo);
    navigate('/checkout');
  };

  // ── Derived totals ────────────────────────────────────────────────────────
  const subtotal = getCartTotal();
  const taxAmount = calculateTax(subtotal);
  const isFreeShipping = shippingCalculated
    ? qualifiesForFreeShipping(subtotal, freeShippingLimit)
    : false;
  const finalShippingCost = isFreeShipping ? 0 : shippingCost;
  const finalTotal = subtotal + taxAmount + (shippingCalculated ? finalShippingCost : 0);
  const isCheckoutDisabled = !shippingCalculated;

  return (
    <div>
      <Header />
      <HeaderSpacer />

      <div className="container">
        <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
          <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
            Home
            <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true"></i>
          </a>
          <span className="stext-109 cl4">Shopping Cart</span>
        </div>
      </div>

      <form className="bg0 p-t-75 p-b-85">
        <div className="container">
          <div className="row">

            {/* ── Cart Table ── */}
            <div className="col-lg-10 col-xl-7 m-lr-auto m-b-50">
              <div className="m-l-25 m-r--38 m-lr-0-xl">
                <div className="wrap-table-shopping-cart">
                  {cartItems.length === 0 ? (
                    <div className="empty-cart-message">
                      <p className="stext-115 cl6 txt-center p-tb-50">Your cart is empty</p>
                      <div className="flex-c-m">
                        <a href="/shop" className="flex-c-m stext-101 cl0 size-107 bg3 bor2 hov-btn3 p-lr-15 trans-04">
                          Continue Shopping
                        </a>
                      </div>
                    </div>
                  ) : (
                    <>
                      <table className="table-shopping-cart">
                        <thead>
                          <tr className="table_head">
                            <th className="column-1">Product</th>
                            <th className="column-2"></th>
                            <th className="column-3">Price</th>
                            <th className="column-4">Quantity</th>
                            <th className="column-5">Total</th>
                            <th className="column-6" style={{ paddingRight: '50px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {cartItems.map((item) => {
                            const effectivePrice = calculateDiscountedPrice(item.price, item.discount);
                            const showItemDiscount = hasDiscount(item.discount);
                            const rowTotal = effectivePrice * item.quantity;

                            return (
                              <tr key={item.id} className="table_row">
                                <td className="column-1">
                                  <div className="how-itemcart1">
                                    <CartImage src={item.image} alt={item.productName} />
                                  </div>
                                </td>
                                <td className="column-2">
                                  <div className="product-info-cart">
                                    <div className="product-name-cart">{item.productName}</div>
                                    {item.variantText && (
                                      <div className="product-variant-cart">{item.variantText}</div>
                                    )}
                                  </div>
                                </td>
                                <td className="column-3">
                                  {showItemDiscount ? (
                                    <div className="cart-price-wrapper">
                                      <span className="cart-price-discounted">
                                        {formatPrice(effectivePrice)}
                                      </span>
                                      <span className="cart-price-original">
                                        {formatPrice(item.price)}
                                      </span>
                                    </div>
                                  ) : (
                                    formatPrice(item.price)
                                  )}
                                </td>
                                <td className="column-4">
                                  <div className="wrap-num-product flex-w m-l-auto m-r-0">
                                    <div
                                      className="btn-num-product-down cl8 hov-btn3 trans-04 flex-c-m"
                                      onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <i className="fs-16 zmdi zmdi-minus"></i>
                                    </div>
                                    <input
                                      className="mtext-104 cl3 txt-center num-product"
                                      type="number"
                                      value={item.quantity}
                                      onChange={(e) => {
                                        const val = parseInt(e.target.value) || 1;
                                        handleUpdateQuantity(item.id, val);
                                      }}
                                      min="1"
                                      max={item.maxStock}
                                    />
                                    <div
                                      className="btn-num-product-up cl8 hov-btn3 trans-04 flex-c-m"
                                      onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <i className="fs-16 zmdi zmdi-plus"></i>
                                    </div>
                                  </div>
                                </td>
                                <td className="column-5">{formatPrice(rowTotal)}</td>
                                <td className="column-6">
                                  <button
                                    className="btn-remove-item-cart"
                                    onClick={(e) => { e.preventDefault(); handleRemoveItem(item.id); }}
                                    type="button"
                                  >
                                    <i className="zmdi zmdi-close"></i>
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      <div className="flex-w flex-sb-m bor15 p-t-18 p-b-15 p-lr-40 p-lr-15-sm">
                        <div className="flex-w flex-m">
                          <div
                            className="flex-c-m stext-101 cl2 size-119 bg8 bor13 hov-btn3 p-lr-15 trans-04 pointer m-tb-10 m-r-10"
                            onClick={handleClearCart}
                          >
                            Clear Cart
                          </div>
                          <a
                            href="/shop"
                            className="flex-c-m stext-101 cl2 size-119 bg8 bor13 hov-btn3 p-lr-15 trans-04 pointer m-tb-10"
                          >
                            Continue Shopping
                          </a>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ── Cart Totals sidebar ── */}
            {cartItems.length > 0 && (
              <div className="col-sm-10 col-lg-7 col-xl-5 m-lr-auto m-b-50">
                <div className="bor10 p-lr-40 p-t-30 p-b-40 m-l-63 m-r-40 m-lr-0-xl p-lr-15-sm">
                  <h4 className="mtext-109 cl2 p-b-30">Cart Totals</h4>

                  {/* Subtotal */}
                  <div className="flex-w flex-t bor12 p-b-13">
                    <div className="size-208">
                      <span className="stext-110 cl2">Subtotal:</span>
                    </div>
                    <div className="size-209">
                      <span className="mtext-110 cl2">{formatPrice(subtotal)}</span>
                    </div>
                  </div>

                  {/* Tax */}
                  <div className="flex-w flex-t bor12 p-t-15 p-b-13">
                    <div className="size-208">
                      <span className="stext-110 cl2">
                        Tax{taxPercentage > 0 ? ` (${taxPercentage}%)` : ''}:
                      </span>
                    </div>
                    <div className="size-209">
                      <span className="mtext-110 cl2">{formatPrice(taxAmount)}</span>
                    </div>
                  </div>

                  {/* Shipping */}
                  <div className="flex-w flex-t bor12 p-t-15 p-b-30">
                    <div className="size-208 w-full-ssm">
                      <span className="stext-110 cl2">Shipping:</span>
                    </div>
                    <div className="size-209 p-r-18 p-r-0-sm w-full-ssm">

                      {/* ── Shipping cost display — shown only after "Update Totals" ── */}
                      {!shippingCalculated ? (
                        <p className="stext-111 cl6 p-t-2">
                          Please calculate shipping to proceed
                        </p>
                      ) : isFreeShipping ? (
                        /* Order qualifies for free shipping */
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            {/* Strike-through original price */}
                            <span style={{
                              textDecoration: 'line-through',
                              color: '#aaa',
                              fontSize: '14px',
                            }}>
                              {formatPrice(shippingCost)}
                            </span>
                            {/* Free label */}
                            <span style={{ color: '#28a745', fontWeight: '700', fontSize: '14px' }}>
                              Free
                            </span>
                          </div>
                          <p style={{ color: '#28a745', fontWeight: '600', fontSize: '12px', margin: 0 }}>
                            🎉 Free Shipping applied (orders over {formatPrice(freeShippingLimit)})
                          </p>
                        </div>
                      ) : (
                        /* Normal shipping cost with free-shipping hint */
                        <div>
                          <p className="stext-111 cl2 p-t-2" style={{ margin: 0 }}>
                            {formatPrice(shippingCost)}
                          </p>
                          {freeShippingLimit > 0 && (
                            <p style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                              Free shipping on orders over {formatPrice(freeShippingLimit)}.
                              Add {formatPrice(freeShippingLimit - subtotal)} more to qualify.
                            </p>
                          )}
                        </div>
                      )}

                      {/* ── Calculate Shipping form ── */}
                      <div className="p-t-15">
                        <span className="stext-112 cl8">Calculate Shipping</span>

                        {loadingCountries ? (
                          <p className="stext-111 cl6 p-t-10">Loading countries...</p>
                        ) : (
                          <>
                            {/* Country */}
                            <div className="m-b-4 m-t-9">
                              <div className={`rs1-select2 rs2-select2 bor8 bg0 ${countryError ? 'input-error-border' : ''}`}>
                                <select
                                  className="js-select2"
                                  name="country"
                                  value={selectedCountry}
                                  onChange={(e) => setSelectedCountry(e.target.value)}
                                >
                                  <option value="">Select a country...</option>
                                  {countries.map((country) => (
                                    <option key={country.id} value={country.id}>
                                      {country.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              {countryError && (
                                <p className="shipping-field-error">{countryError}</p>
                              )}
                            </div>

                            {/* State / Province */}
                            <div className="m-b-4">
                              <div className={`rs1-select2 bor8 bg0 ${stateError ? 'input-error-border' : ''}`}>
                                <select
                                  className="js-select2"
                                  name="state"
                                  value={selectedState}
                                  onChange={(e) => setSelectedState(e.target.value)}
                                  disabled={!selectedCountry || states.length === 0}
                                >
                                  <option value="">
                                    {states.length === 0 && selectedCountry
                                      ? 'No states available'
                                      : 'Select state / province...'}
                                  </option>
                                  {states.map((state) => (
                                    <option key={state.id} value={state.id}>
                                      {state.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              {stateError && (
                                <p className="shipping-field-error">{stateError}</p>
                              )}
                            </div>

                            {/* Postal code */}
                            <div className="bor8 bg0 m-b-22">
                              <input
                                className="stext-111 cl8 plh3 size-111 p-lr-15"
                                type="text"
                                name="postcode"
                                placeholder="Postcode / Zip"
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                              />
                            </div>

                            {/* Update Totals button */}
                            <div className="flex-w">
                              <div
                                className="flex-c-m stext-101 cl2 size-115 bg8 bor13 hov-btn3 p-lr-15 trans-04 pointer"
                                onClick={handleUpdateShipping}
                              >
                                Update Totals
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="flex-w flex-t p-t-27 p-b-33">
                    <div className="size-208">
                      <span className="mtext-101 cl2">Total:</span>
                    </div>
                    <div className="size-209 p-t-1">
                      <span className="mtext-110 cl2">
                        {shippingCalculated ? formatPrice(finalTotal) : '—'}
                      </span>
                    </div>
                  </div>

                  {/* Proceed to Checkout */}
                  <button
                    type="button"
                    className={`flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 ${isCheckoutDisabled ? 'btn-disabled' : 'pointer'}`}
                    onClick={handleCheckout}
                    disabled={isCheckoutDisabled}
                  >
                    Proceed to Checkout
                  </button>

                  {isCheckoutDisabled && (
                    <p className="stext-111 cl6 txt-center p-t-10" style={{ fontSize: '12px' }}>
                      Please calculate shipping first
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </form>

      <Footer />
      <BackToTop />

      <style>{`
        /* ── Cart image ── */
        .how-itemcart1 {
          width: 100px;
          height: 100px;
          overflow: hidden;
          flex-shrink: 0;
        }
        .how-itemcart1 img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        /* ── Cart price discount styles ── */
        .cart-price-wrapper {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .cart-price-discounted {
          font-weight: 600;
          color: inherit;
        }
        .cart-price-original {
          text-decoration: line-through;
          color: #aaa;
          font-size: 12px;
          font-weight: 400;
        }
        .cart-discount-badge {
          display: inline-block;
          background: #e74c3c;
          color: #fff;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.8px;
          padding: 1px 6px;
          border-radius: 2px;
          width: fit-content;
        }

        /* ── Select widget ── */
        .rs1-select2 {
          position: relative;
        }
        .bor8 { border: 1px solid #e6e6e6; }
        .rs1-select2 select {
          width: 100%;
          padding: 10px 15px;
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 13px;
          color: #555;
          appearance: none;
          font-family: Poppins-Regular, sans-serif;
        }
        .rs1-select2 select:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* ── Validation error styles ── */
        .input-error-border {
          border-color: #e74c3c !important;
        }
        .shipping-field-error {
          color: #e74c3c;
          font-size: 11px;
          margin: 4px 0 8px 2px;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .shipping-field-error::before {
          content: '⚠';
          font-size: 11px;
        }

        /* ── Misc ── */
        .empty-cart-message {
          padding: 40px 20px;
          text-align: center;
        }
        .product-info-cart {
          display: flex;
          flex-direction: column;
        }
        .product-name-cart {
          font-weight: 500;
          margin-bottom: 5px;
        }
        .product-variant-cart {
          font-size: 12px;
          color: #888;
        }
        .btn-remove-item-cart {
          background: none;
          border: none;
          cursor: pointer;
          color: #888;
          font-size: 20px;
          padding: 5px 10px;
          transition: color 0.3s;
        }
        .btn-remove-item-cart:hover {
          color: #e65540;
        }
        .btn-disabled {
          opacity: 0.5;
          cursor: not-allowed !important;
          pointer-events: none;
        }

        /* ── Spacing helpers for error layout ── */
        .m-b-4 { margin-bottom: 4px; }
      `}</style>
    </div>
  );
};

export default Cart;